import { getProspectEducationSorted, getProspectExperiencesSorted } from '../../../../../../helpers/prospects';
import { getDateString } from '../../../../../../services/utils/timeHelper';
import { ProspectDataDto, ProspectDto } from '@zaplify/prospects';

export function getContactData(prospect: ProspectDataDto & { id?: string }) {
    const { phoneNumbers, experiences, education, linkedinProfileVerification, originalData, ...prospectData } =
        prospect;

    //getting stringified education & experiences & phonenumbers
    const formattedExperiences = getProspectExperiences(experiences);
    const formattedEducation = getProspectEducation(education);
    const formattedPhoneNumbers = getProspectPhoneNumbers(phoneNumbers);

    //Replacing hashtags cause csv breaks otherwise
    const linkedinDescription = prospectData?.linkedinDescription?.replace(/#/g, '');
    prospectData.linkedinDescription = linkedinDescription;

    const newOriginalData = {};
    // To all future developers. Sorry.
    if (originalData) {
        const originalDataFormatted = getContactData(originalData);
        for (const key in originalDataFormatted) {
            if (key === 'id') continue;

            newOriginalData[`originalData.${key}`] = originalDataFormatted[key];
        }
        for (const k of Object.keys(prospectData)) {
            const key = k as keyof (ProspectDataDto & { id?: string; rowNumber?: number });
            if (key === 'id') continue;

            // Add a field for if the key has changed from the original
            if (key !== 'rowNumber') {
                if (key === 'phoneNumbers') {
                    const allSame = phoneNumbers.every((phoneNumber, index) => {
                        return isPhoneNumberSame(phoneNumber.number, originalData.phoneNumbers[index].number);
                    });
                    newOriginalData[`originalData.${key}.changed`] = !allSame;
                } else if (prospectData[key] !== originalData[key]) {
                    newOriginalData[`originalData.${key}.changed`] = true;
                } else {
                    newOriginalData[`originalData.${key}.changed`] = false;
                }
            }
        }
    }

    return {
        id: prospect.id,
        ...prospectData,
        ...formattedPhoneNumbers,
        ...formattedExperiences,
        ...formattedEducation,
        ...newOriginalData,
    };
}

function isPhoneNumberSame(phoneNumber1: string, phoneNumber2: string) {
    const p1 = cleanPhoneNumber(phoneNumber1)?.replaceAll('0', '');
    const p2 = cleanPhoneNumber(phoneNumber2)?.replaceAll('0', '');

    const shortestPhoneNumbers = p1.length < p2.length ? p1 : p2;
    const longestPhoneNumber = p1.length < p2.length ? p2 : p1;

    return longestPhoneNumber?.endsWith(shortestPhoneNumbers);
}

function cleanPhoneNumber(phoneNumber: string) {
    return phoneNumber?.replace(/^\+/, '00')?.replace(/\D/g, '');
}

function getObjectKeysFromArray(data: Array<object>): string[] {
    const dataObjectKeys = [];
    data.forEach((item) => {
        Object.keys(item).forEach((key) => {
            if (!dataObjectKeys.includes(key)) {
                dataObjectKeys.push(key);
            }
        });
    });
    return dataObjectKeys;
}

export function getProspectExperiences(experiences) {
    if (Array.isArray(experiences) && experiences.length) {
        return experiences
            .sort((a, b) => getProspectExperiencesSorted(a, b))
            .reduce(
                (acc, curr, idx) => ({
                    ...acc,
                    [`experience ${idx + 1}`]: JSON.stringify(curr),
                }),
                {}
            );
    } else {
        return {};
    }
}

export function getProspectEducation(education) {
    if (Array.isArray(education) && education.length) {
        return education
            .sort((a, b) => getProspectEducationSorted(a, b))
            .reduce(
                (acc, curr, idx) => ({
                    ...acc,
                    [`education ${idx + 1}`]: JSON.stringify(curr),
                }),
                {}
            );
    } else {
        return {};
    }
}

export function getProspectPhoneNumbers(phoneNumbers) {
    if (Array.isArray(phoneNumbers) && phoneNumbers.length) {
        return phoneNumbers.reduce(
            (acc, curr, idx) => ({
                ...acc,
                [`phoneNumber ${idx + 1}`]: cleanPhoneNumber(curr?.number),
            }),
            {}
        );
    } else {
        return {};
    }
}

//Returns file name for prospect csv file. Format - "zaplify-prospects-CAMPIGNNAME-DATE"
export function getFileName() {
    let fileName = 'contacts';
    fileName += '-';
    fileName += getDateString();

    return fileName;
}
